import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { usePortal, PORTAL_RENTALS, PORTAL_TRUCKING } from "@/context/PortalContext";
import { Button } from "@/components/ui/button";
import { LOGO_URL, CAR_2012_URL, WATER_TRUCK_URL, CONTACT } from "@/lib/constants";
import { Car, Truck, ArrowRight, ShieldCheck } from "lucide-react";
import { useEffect } from "react";

export default function PortalSelectPage() {
  const { user, loading } = useAuth();
  const { setPortal } = usePortal();
  const nav = useNavigate();

  useEffect(() => {
    if (!loading && !user) nav("/login");
  }, [loading, user, nav]);

  const enter = (p) => {
    setPortal(p);
    if (user?.active_role && user.active_role !== "customer") {
      nav("/dashboard");
    } else {
      nav(p === PORTAL_RENTALS ? "/portal/rentals" : "/portal/trucking");
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col">
      <div className="border-b border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="logo" className="h-10 w-10 object-contain" />
            <div className="leading-tight">
              <div className="font-heading font-black text-base text-slate-900">CLEAVIE'S</div>
              <div className="text-[10px] tracking-[0.22em] font-bold text-[#1D4ED8] -mt-0.5">ENTERPRISE</div>
            </div>
          </Link>
          <div className="text-sm text-slate-600">Signed in as <b className="text-slate-900">{user.email}</b></div>
        </div>
      </div>
      <div className="flex-1 flex items-center">
        <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="tag-overline">Choose your portal</div>
            <h1 className="font-heading font-black text-4xl sm:text-5xl text-slate-900 mt-3">
              Hi {user.name?.split(" ")[0] || "there"} — what do you need today?
            </h1>
            <p className="text-slate-600 mt-3">
              One account, two service experiences. Switch between portals anytime.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            <motion.button
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
              whileHover={{ y: -4 }}
              transition={{ duration: 0.3 }}
              onClick={() => enter(PORTAL_RENTALS)}
              data-testid="portal-card-rentals"
              className="group text-left bg-white border-2 border-slate-200 hover:border-[#E32636] rounded-md overflow-hidden transition-all"
            >
              <div className="aspect-[16/9] bg-gradient-to-br from-slate-50 to-white flex items-center justify-center p-8">
                <img src={CAR_2012_URL} alt="vehicles" className="max-h-full object-contain" />
              </div>
              <div className="p-7">
                <div className="flex items-center gap-2 text-[#E32636]">
                  <Car className="h-6 w-6" strokeWidth={2.5} />
                  <span className="tag-overline !text-[#E32636]">Portal 1</span>
                </div>
                <h2 className="font-heading font-black text-2xl mt-2 text-slate-900">Vehicle Rentals & Leasing</h2>
                <p className="text-sm text-slate-600 mt-2">Daily rentals from EC$130 · Lease applications · Document tracking · Payments.</p>
                <div className="mt-5 inline-flex items-center gap-1 font-bold text-slate-900">Enter portal <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" /></div>
              </div>
            </motion.button>

            <motion.button
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
              whileHover={{ y: -4 }}
              transition={{ duration: 0.3, delay: 0.1 }}
              onClick={() => enter(PORTAL_TRUCKING)}
              data-testid="portal-card-trucking"
              className="group text-left bg-[#0F172A] text-white border-2 border-slate-900 hover:border-[#FFCE00] rounded-md overflow-hidden transition-all"
            >
              <div className="aspect-[16/9] bg-slate-800 overflow-hidden">
                <img src={WATER_TRUCK_URL} alt="trucking" className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition" />
              </div>
              <div className="p-7">
                <div className="flex items-center gap-2 text-[#FFCE00]">
                  <Truck className="h-6 w-6" strokeWidth={2.5} />
                  <span className="tag-overline !text-[#FFCE00]">Portal 2</span>
                </div>
                <h2 className="font-heading font-black text-2xl mt-2">Trucking & Industrial Services</h2>
                <p className="text-sm text-slate-300 mt-2">Water · Bins · Diagnostics · Aggregate · Sand · Equipment hauling.</p>
                <div className="mt-5 inline-flex items-center gap-1 font-bold">Enter portal <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" /></div>
              </div>
            </motion.button>
          </div>

          <div className="mt-10 flex items-center justify-center gap-2 text-xs text-slate-500">
            <ShieldCheck className="h-4 w-4 text-emerald-600" /> One Cleavie's Enterprise account · Switch portals anytime
          </div>
        </div>
      </div>
    </div>
  );
}
